import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Radio, CheckCircle2, Eye, Send, ChevronRight, AlertTriangle } from 'lucide-react';

/**
 * Admin dashboard widget — list of recent ScaleBoard Radio drops with
 * engagement stats (delivered / opened / completed / flagged).
 */
export default function RadioDropsPanel() {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/radio/drops', { params: { limit: 50 } })
      .then((r) => setDrops(r.data.items || []))
      .catch(() => setDrops([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="rounded-xl border bg-white p-4" style={{ borderColor: '#E5E1DC' }} data-testid="radio-drops-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#2A7B88' }}>
          <Radio size={15} style={{ color: '#FFF' }} />
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>ScaleBoard Radio — Drops</p>
          <p className="text-[11px]" style={{ color: '#6B7280' }}>Every tokenized training sent via WhatsApp · per-recipient tracking</p>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-center py-6" style={{ color: '#9CA3AF' }}>Loading drops…</p>
      ) : drops.length === 0 ? (
        <div className="text-center py-8">
          <Send size={22} className="mx-auto mb-2" style={{ color: '#9CA3AF' }} />
          <p className="text-xs font-medium" style={{ color: '#6B7280' }}>No drops sent yet</p>
          <p className="text-[11px] mt-1" style={{ color: '#9CA3AF' }}>Go to Training Center → open any SOP → click <strong style={{ color: '#2A7B88' }}>📻 Radio</strong> to send your first drop.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {drops.map((d) => {
            const opened = d.total_opened || 0;
            const targets = d.total_targets || 1;
            const pct = Math.round((opened / targets) * 100);
            return (
              <button key={d.drop_id} onClick={() => setSelected(d.drop_id)}
                className="w-full text-left px-3 py-2 rounded-md border hover:bg-[#F7F5F2] transition flex items-center gap-3"
                style={{ borderColor: '#F3F0EB' }}
                data-testid={`radio-drop-${d.drop_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: '#1B2A4A' }}>{d.sop_title}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>
                    {new Date(d.sent_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })} · {d.sent_by || '—'} · {d.lang?.toUpperCase() || '—'}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF' }}>
                    <Send size={9} /> {d.total_delivered}/{d.total_targets}
                  </span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#8C560F' }}>
                    <Eye size={9} /> {opened}
                  </span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#ECFDF5', color: '#166534' }}>
                    <CheckCircle2 size={9} /> {d.total_completed}
                  </span>
                  <span className="font-semibold" style={{ color: pct >= 80 ? '#2D8B5E' : pct >= 40 ? '#BA7517' : '#D4726A' }}>
                    {pct}%
                  </span>
                </div>
                <ChevronRight size={14} style={{ color: '#9CA3AF' }} />
              </button>
            );
          })}
        </div>
      )}

      {selected && <DropDetailModal dropId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function DropDetailModal({ dropId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/radio/drops/${dropId}`).then((r) => setData(r.data)).catch(() => {});
  }, [dropId]);
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-start justify-between" style={{ borderColor: '#E5E1DC' }}>
          <div>
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{data.drop.sop_title}</p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              {new Date(data.drop.sent_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })} · {data.drop.lang?.toUpperCase()} · {data.recipients.length} recipients
            </p>
          </div>
          <button onClick={onClose} style={{ color: '#6B7280' }}>✕</button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-1.5">
          {data.recipients.map((r) => (
            <div key={r.person_id} className="flex items-center justify-between px-3 py-2 rounded-md border" style={{ borderColor: '#F3F0EB' }}>
              <div>
                <p className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{r.name}</p>
                <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{r.phone_masked || 'no phone'}</p>
              </div>
              <div className="flex items-center gap-1.5 text-[10px]">
                {r.flagged_shared && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}
                    title="Opened from >3 different devices — possibly forwarded"
                  >
                    <AlertTriangle size={9} /> shared
                  </span>
                )}
                {r.completed ? (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#ECFDF5', color: '#166534' }}>
                    <CheckCircle2 size={10} /> listened ({r.open_count}×)
                  </span>
                ) : r.opened ? (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#8C560F' }}>
                    <Eye size={10} /> opened
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}>pending</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
